<template>
  <div class="calendar-container">
    <!-- Header Section -->
    <div class="calendar-header">
      <div class="header-content">
        <div class="header-text">
          <h1 class="calendar-title">Calendar Activități</h1>
          <p class="calendar-subtitle">Gestionează întâlniri, apeluri și evenimente</p>
        </div>
        <div class="header-actions">
          <v-btn
            :color="APP_Color"
            prepend-icon="mdi-refresh"
            variant="text"
            @click="refreshCalendar"
          >
            Actualizează
          </v-btn>
          <v-btn
            :color="APP_Color"
            prepend-icon="mdi-plus"
            variant="elevated"
          >
            Eveniment Nou
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Quick Stats -->
    <v-row class="stats-row">
      <v-col v-for="(stat, index) in quickStats" :key="index" cols="12" sm="6" md="3">
        <v-card class="stat-card" elevation="2">
          <div class="stat-icon-wrapper" :style="{ background: stat.bgColor }">
            <v-icon :color="stat.color" size="28">{{ stat.icon }}</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stat.value }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Calendar Card -->
    <v-card class="calendar-card" elevation="2">
      <v-card-title class="card-title">
        <v-icon :color="APP_Color" size="24" class="mr-2">mdi-calendar-month</v-icon>
        <span>Calendar</span>
      </v-card-title>
      <v-card-text class="pa-4">
        <DxScheduler 
          ref="dataGridRef" 
          time-zone="Europe/Bucharest" 
          :data-source="dataSource"
          date-serialization-format="yyyy-MM-ddTHH:mm:ssZ" 
          :current-date="currentDate" 
          :views="views" 
          :height="650"
          :start-day-hour="9" 
          :end-day-hour="19" 
          start-date-expr="StartDate" 
          end-date-expr="EndDate" 
          all-day-expr="AllDay"
          text-expr="Subject" 
          description-expr="Description" 
          label-expr="Label" 
          reminder-rule-expr="ReminderInfo"
          recurrence-rule-expr="RecurrenceInfo" 
          current-view="month" 
          @appointmentDeleted="showDeletedToast"
        >
          <DxLoadPanel :enabled="true" :indicator-src="'reload6.gif'" />
        </DxScheduler>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import DxScheduler from 'devextreme-vue/scheduler';
import ODataStore from 'devextreme/data/odata/store';
import AppSetting from '@/settings/appSettings';
import { defineComponent } from 'vue';
import notify from 'devextreme/ui/notify';
import CrmAppointments from '@/services/Comercial/CrmAppointments';
import 'devextreme/localization/messages/ro.json';
import { loadMessages, locale } from 'devextreme/localization';
import { DxLoadPanel } from 'devextreme-vue/data-grid';

export default defineComponent({
  components: {
    DxScheduler,
    DxLoadPanel
  },
  data() {
    return {
      APP_Color: AppSetting.APP_Color,
      views: ['day', 'week', 'month', 'agenda'],
      currentDate: new Date(),
      dataSource: null,
      quickStats: [
        { label: 'Astăzi', value: '5', icon: 'mdi-calendar-today', color: '#ff5722', bgColor: 'rgba(255, 87, 34, 0.1)' },
        { label: 'Săptămâna Aceasta', value: '18', icon: 'mdi-calendar-week', color: '#2196F3', bgColor: 'rgba(33, 150, 243, 0.1)' },
        { label: 'Luna Aceasta', value: '47', icon: 'mdi-calendar-month', color: '#4CAF50', bgColor: 'rgba(76, 175, 80, 0.1)' },
        { label: 'Viitoare', value: '23', icon: 'mdi-calendar-clock', color: '#9C27B0', bgColor: 'rgba(156, 39, 176, 0.1)' }
      ]
    };
  },
  mounted() {
    this.dataSource = {
      store: new ODataStore({
        url: AppSetting.API_Url_ComercialOdata + 'CRM_Appointments',
        key: 'ID',
        version: 4,
        beforeSend: function (e) {
          e.headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
          };
        },
      }),
    };
    loadMessages(require('devextreme/localization/messages/ro.json'));
    locale('ro');
  },
  methods: {
    refreshCalendar() {
      this.$refs.dataGridRef?.instance?.getDataSource()?.reload();
      notify('Calendar actualizat!', 'success', 1000);
    },

    showDeletedToast() {
      const savePromise = this.$refs.dataGridRef?.instance?.saveEditData();
      const body = this.formData;
      CrmAppointments.delete(body).then((response) => {
        this.isModalVisible = false;
        notify('Datele au fost salvate cu succes!', 'success', 1000);
        this.formData = {
          Type: '',
          StartDate: '',
          EndDate: '',
          AllDay: '',
          Subject: '',
          Location: '',
          Description: '',
          Status: '',
          Label: '',
          ContactID: '',
          ReminderInfo: '',
          ReccurenceInfo: '',
          Utilizator: '',
          AlocatDe: '',
        };
      }).catch((error) => {
        console.error(error);
      });
      this.dataSource();
    },
  },
});
</script>

<style scoped>
.calendar-container {
  padding: 2rem;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
  min-height: 100vh;
}

/* ========== HEADER ========== */
.calendar-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.calendar-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.calendar-subtitle {
  font-size: 1rem;
  color: #718096;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

/* ========== STATS CARDS ========== */
.stats-row {
  margin-bottom: 1.5rem;
}

.stat-card {
  border-radius: 16px !important;
  padding: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12) !important;
}

.stat-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.875rem;
  color: #718096;
  margin-top: 0.25rem;
}

/* ========== CALENDAR CARD ========== */
.calendar-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.card-title {
  display: flex;
  align-items: center;
  font-weight: 600 !important;
  font-size: 1.125rem !important;
  padding: 1.5rem !important;
  border-bottom: 1px solid #e0e0e0;
  background: white;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 960px) {
  .calendar-container {
    padding: 1rem;
  }

  .calendar-title {
    font-size: 1.5rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions .v-btn {
    flex: 1;
  }
}

@media (max-width: 600px) {
  .stat-card {
    flex-direction: column;
    text-align: center;
  }

  .stat-icon-wrapper {
    margin: 0 auto;
  }
}
</style>
